import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Form, Formik, useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';
import { Button } from 'primereact/button';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import AddKssText from './AddKssText';
import SaveContentConfirmationDialog from '../components/SaveContentConfirmationDialog';

const i18nMessages = defineMessages({
  surveyItemCode: {
    id: 'menu.item-bank-management.survey-item-code',
    defaultMessage: 'Survey Item Code',
  },
  kssTextType: {
    id: 'menu.item-bank-management.kss-text-type',
    defaultMessage: 'KSS Text Type',
  },
  level: {
    id: 'menu.item-bank-management.level',
    defaultMessage: 'Level',
  },
  sequence: {
    id: 'menu.item-bank-management.sequence',
    defaultMessage: 'Sequence',
  },
  text: {
    id: 'menu.item-bank-management.text',
    defaultMessage: 'Text',
  },
  actions: {
    id: 'menu.item-bank-management.actions',
    defaultMessage: 'Actions',
  },
});

const AddKssTexts = ({
  availableKssTextTypes,
  availableTextLevels,
  availableSequences,
  kssTextValues,
  processFormValues,
}) => {
  const [index, setIndex] = useState(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const { formatMessage } = useIntl();
  const history = useHistory();
  const { values: parentFormValues, setFieldValue, isSubmitting } = useFormikContext();

  const kssTextsValues = parentFormValues.kssTexts;
  const surveyItemValues = parentFormValues.surveyItems;

  const initialValues =
    index != null
      ? {
          itemCode: kssTextsValues[index].itemCode,
          text: kssTextsValues[index].text,
          kssTextType: kssTextsValues[index].kssTextType,
          level: kssTextsValues[index].level,
          sequence: kssTextsValues[index].sequence,
        }
      : {
          itemCode: kssTextValues.itemCode,
          text: '',
          kssTextType: kssTextValues.kssTextType,
          level: kssTextValues.level,
          sequence: kssTextValues.sequence,
        };

  const validationSchema = Yup.object().shape({
    itemCode: Yup.string().required(),
    text: Yup.string().required(),
    kssTextType: Yup.string().required(),
    level: Yup.string().required(),
    sequence: Yup.string().required(),
  });

  const actionsBody = (data, { rowIndex }) => (
    <div className="align-center">
      <Button type="button" className="p-button-secondary" icon="pi pi-pencil" onClick={() => setIndex(rowIndex)} />
    </div>
  );

  return (
    <>
      <SaveContentConfirmationDialog
        showConfirmationDialog={showConfirmationDialog}
        setShowConfirmationDialog={setShowConfirmationDialog}
        isSubmitting={isSubmitting}
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, formikBag) => {
          formikBag.resetForm();

          if (index != null) {
            const updatedValues = kssTextsValues.map((reportText, kssTextIndex) => {
              if (kssTextIndex === index) {
                return { ...values };
              }

              return reportText;
            });

            setFieldValue('kssTexts', updatedValues);
            setIndex(null);
          } else {
            setFieldValue('kssTexts', [...parentFormValues.kssTexts, { ...values }]);

            processFormValues(surveyItemValues, values);
          }
        }}
      >
        <Form>
          <AddKssText
            surveyItems={surveyItemValues}
            availableKssTextTypes={availableKssTextTypes}
            availableTextLevels={availableTextLevels}
            availableSequences={availableSequences}
          />
          <FormattedMessage id="item-bank-management.context-type.save-kss-text" defaultMessage="Save KSS Text">
            {(text) => <Button type="submit" className="p-button-secondary inner-form-submit-button" label={text} />}
          </FormattedMessage>
        </Form>
      </Formik>
      <DataTable value={kssTextsValues} className="text-center">
        <Column field="itemCode" header={formatMessage(i18nMessages.surveyItemCode)} />
        <Column field="kssTextType" header={formatMessage(i18nMessages.kssTextType)} />
        <Column field="level" header={formatMessage(i18nMessages.level)} />
        <Column field="sequence" header={formatMessage(i18nMessages.sequence)} />
        <Column field="text" header={formatMessage(i18nMessages.text)} />
        <Column body={actionsBody} header={formatMessage(i18nMessages.actions)} />
      </DataTable>
      <div className="outer-form-submit-button">
        <FormattedMessage id="actions.save" defaultMessage="Save">
          {(text) => <Button type="button" label={text} onClick={() => setShowConfirmationDialog(true)} />}
        </FormattedMessage>
        <FormattedMessage id="actions.back" defaultMessage="Back">
          {(text) => (
            <Button type="button" onClick={() => history.goBack()} className="p-button-secondary" label={text} />
          )}
        </FormattedMessage>
      </div>
    </>
  );
};

AddKssTexts.propTypes = {
  availableKssTextTypes: PropTypes.arrayOf(PropTypes.any).isRequired,
  availableTextLevels: PropTypes.arrayOf(PropTypes.any).isRequired,
  availableSequences: PropTypes.arrayOf(PropTypes.any).isRequired,
  kssTextValues: PropTypes.objectOf(PropTypes.any).isRequired,
  processFormValues: PropTypes.func.isRequired,
};

export default AddKssTexts;
